import React from "react";
import { getPdfRenderer } from "../../getPdfRenderer";


// Hilfsfunktion um CSS transform in PDF-kompatible Properties zu konvertieren
export const parseTransformForPDF = (transform?: string, originalWidth?: number | string, originalHeight?: number | string): {
    left?: number;
    top?: number;
    position?: "absolute";
    width?: number | string;
    height?: number | string;
    transform?: string;
} => {
    if (!transform) return {};

    const result: any = {};

    // Translate extrahieren: translate(10px, 20px) oder translateX(10px) oder translateY(20px)
    const translateMatch = transform.match(/translate\(([^,]+)(?:,\s*([^)]+))?\)/);
    const translateXMatch = transform.match(/translateX\(([^)]+)\)/);
    const translateYMatch = transform.match(/translateY\(([^)]+)\)/);

    let translateX = 0, translateY = 0;

    if (translateMatch) {
        translateX = parseFloat(translateMatch[1]) || 0;
        translateY = parseFloat(translateMatch[2] || "0") || 0;
    }
    if (translateXMatch) {
        translateX = parseFloat(translateXMatch[1]) || 0;
    }
    if (translateYMatch) {
        translateY = parseFloat(translateYMatch[1]) || 0;
    }

    if (translateX !== 0 || translateY !== 0) {
        result.position = "absolute";
        result.left = translateX;
        result.top = translateY;
    }

    // Scale extrahieren: scale(2) oder scale(1.5, 0.8) oder scaleX(2) oder scaleY(0.5)
    const scaleMatch = transform.match(/scale\(([^,]+)(?:,\s*([^)]+))?\)/);
    const scaleXMatch = transform.match(/scaleX\(([^)]+)\)/);
    const scaleYMatch = transform.match(/scaleY\(([^)]+)\)/);

    let scaleX = 1, scaleY = 1;

    if (scaleMatch) {
        scaleX = parseFloat(scaleMatch[1]) || 1;
        scaleY = parseFloat(scaleMatch[2] || scaleMatch[1]) || scaleX; // Wenn nur ein Wert, für beide Achsen verwenden
    }
    if (scaleXMatch) {
        scaleX = parseFloat(scaleXMatch[1]) || 1;
    }
    if (scaleYMatch) {
        scaleY = parseFloat(scaleYMatch[1]) || 1;
    }

    // Scale in width/height umwandeln
    if (scaleX !== 1 || scaleY !== 1) {
        if (originalWidth && typeof originalWidth === 'number') {
            result.width = originalWidth * scaleX;
        } else if (originalWidth && typeof originalWidth === 'string') {
            const numWidth = parseFloat(originalWidth);
            if (!isNaN(numWidth)) {
                result.width = `${numWidth * scaleX}${originalWidth.replace(/[\d.]/g, '')}`;
            }
        }

        if (originalHeight && typeof originalHeight === 'number') {
            result.height = originalHeight * scaleY;
        } else if (originalHeight && typeof originalHeight === 'string') {
            const numHeight = parseFloat(originalHeight);
            if (!isNaN(numHeight)) {
                result.height = `${numHeight * scaleY}${originalHeight.replace(/[\d.]/g, '')}`;
            }
        }
    }

    // Andere Transforms (rotate) als String beibehalten für Fallback - scale und translate wurden bereits behandelt
    const otherTransforms = transform
        .replace(/translate\([^)]*\)/g, '')
        .replace(/translateX\([^)]*\)/g, '')
        .replace(/translateY\([^)]*\)/g, '')
        .replace(/scale\([^)]*\)/g, '')
        .replace(/scaleX\([^)]*\)/g, '')
        .replace(/scaleY\([^)]*\)/g, '')
        .trim();

    if (otherTransforms) {
        result.transform = otherTransforms;
    }

    return result;
};

// Hilfsfunktion um undefined Properties aus Style-Objekten zu entfernen
export const removeUndefinedProperties = (obj: any): any => {
    const cleaned: any = {};
    for (const key in obj) {
        if (obj[key] !== undefined) {
            cleaned[key] = obj[key];
        }
    }
    return cleaned;
};

// Hilfsfunktion um CSS-Werte für PDF zu bereinigen
const sanitizeValueForPDF = (value: any): any => {
    if (value === undefined || value === null) return undefined;

    // String-Werte, die PDF nicht versteht
    if (typeof value === 'string') {
        switch (value) {
            case 'max-content':
            case 'min-content':
            case 'fit-content':
            case 'auto':
                return undefined; // Entferne diese Werte für PDF
            case 'inherit':
            case 'initial':
            case 'unset':
                return undefined;
            default:
                return value;
        }
    }

    return value;
};

// Gemeinsame Funktion um React.CSSProperties in PDF-kompatible Properties zu konvertieren
export const convertCSSPropertiesForPDF = (style: React.CSSProperties, transformParsed: any): any => {
    // Debug-Warnung für problematische Properties
    if (style.lineHeight !== undefined) {
        console.warn('PDF CSS Filter: lineHeight wird für View-Komponenten entfernt, da es Text-Rendering in Child-Text-Komponenten verhindert. Verwende lineHeight direkt in RText-Komponenten.', style.lineHeight);
    }
    if (style.minWidth !== undefined || style.minHeight !== undefined || style.maxWidth !== undefined || style.maxHeight !== undefined) {
        console.warn('PDF CSS Filter: minWidth, minHeight, maxWidth, maxHeight werden entfernt - nicht unterstützt von @react-pdf/renderer. Verwende feste width/height Werte.', {
            minWidth: style.minWidth,
            minHeight: style.minHeight,
            maxWidth: style.maxWidth,
            maxHeight: style.maxHeight
        });
    }

    const rawPdfStyle = {
        // Layout
        display: style.display === "none" ? "none" : undefined,
        position: transformParsed.position || (style.position === "absolute" ? "absolute" : undefined),
        top: transformParsed.top !== undefined ? transformParsed.top : style.top,
        right: style.right,
        bottom: style.bottom,
        left: transformParsed.left !== undefined ? transformParsed.left : style.left,
        zIndex: style.zIndex,

        // Flexbox - nur PDF-kompatible Werte
        flexDirection: (style.flexDirection === "row" || style.flexDirection === "column" || style.flexDirection === "row-reverse" || style.flexDirection === "column-reverse"
            ? style.flexDirection : (style.display === "flex" ? "row" : undefined)) as any,
        justifyContent: (style.justifyContent === "flex-start" || style.justifyContent === "center" || style.justifyContent === "flex-end" || style.justifyContent === "space-between" || style.justifyContent === "space-around" || style.justifyContent === "space-evenly"
            ? style.justifyContent : undefined) as any,
        alignItems: (style.alignItems === "flex-start" || style.alignItems === "center" || style.alignItems === "flex-end" || style.alignItems === "stretch" || style.alignItems === "baseline"
            ? style.alignItems : undefined) as any,
        alignSelf: (style.alignSelf === "flex-start" || style.alignSelf === "center" || style.alignSelf === "flex-end" || style.alignSelf === "stretch" || style.alignSelf === "baseline"
            ? style.alignSelf : undefined) as any,
        flex: typeof style.flex === "number" ? style.flex : undefined,
        flexGrow: style.flexGrow,
        flexShrink: style.flexShrink,
        flexBasis: style.flexBasis,
        flexWrap: (style.flexWrap === "nowrap" || style.flexWrap === "wrap" || style.flexWrap === "wrap-reverse"
            ? style.flexWrap : undefined) as any,

        // Sizing - mit Scale-Transform-Anpassung und CSS-Value-Bereinigung
        width: transformParsed.width !== undefined
            ? transformParsed.width
            : sanitizeValueForPDF(style.width),
        height: transformParsed.height !== undefined
            ? transformParsed.height
            : sanitizeValueForPDF(style.height),
        // minWidth und minHeight werden nicht von @react-pdf/renderer unterstützt
        // minWidth: sanitizeValueForPDF(style.minWidth),
        // maxWidth: sanitizeValueForPDF(style.maxWidth),  
        // minHeight: sanitizeValueForPDF(style.minHeight),
        // maxHeight: sanitizeValueForPDF(style.maxHeight),

        // Spacing - React CSSProperties hat keine paddingHorizontal/paddingVertical
        padding: style.padding,
        paddingTop: style.paddingTop,
        paddingRight: style.paddingRight,
        paddingBottom: style.paddingBottom,
        paddingLeft: style.paddingLeft,
        margin: style.margin,
        marginTop: style.marginTop,
        marginRight: style.marginRight,
        marginBottom: style.marginBottom,
        marginLeft: style.marginLeft,

        // Background & Colors
        backgroundColor: style.backgroundColor,
        color: style.color,
        opacity: typeof style.opacity === "number" ? style.opacity : (typeof style.opacity === "string" ? parseFloat(style.opacity) : undefined),

        // Borders - nur PDF-kompatible Werte
        borderWidth: style.borderWidth,
        borderColor: style.borderColor,
        borderStyle: (style.borderStyle === "solid" || style.borderStyle === "dashed" || style.borderStyle === "dotted"
            ? style.borderStyle : undefined) as any,
        borderRadius: style.borderRadius,
        borderTopWidth: style.borderTopWidth,
        borderRightWidth: style.borderRightWidth,
        borderBottomWidth: style.borderBottomWidth,
        borderLeftWidth: style.borderLeftWidth,
        borderTopColor: style.borderTopColor,
        borderRightColor: style.borderRightColor,
        borderBottomColor: style.borderBottomColor,
        borderLeftColor: style.borderLeftColor,
        borderTopLeftRadius: style.borderTopLeftRadius,
        borderTopRightRadius: style.borderTopRightRadius,
        borderBottomLeftRadius: style.borderBottomLeftRadius,
        borderBottomRightRadius: style.borderBottomRightRadius,

        // Text & Font - nur PDF-kompatible Werte
        fontSize: style.fontSize,
        fontFamily: style.fontFamily,
        fontWeight: style.fontWeight as any,
        fontStyle: (style.fontStyle === "normal" || style.fontStyle === "italic" ? style.fontStyle : undefined) as any,
        // lineHeight wird für View-Komponenten gefiltert, da es Text-Rendering in Child-Text-Komponenten verhindert
        // lineHeight: style.lineHeight,  // PROBLEMATISCH - verhindert Text in RText Children!
        textAlign: (style.textAlign === "left" || style.textAlign === "center" || style.textAlign === "right" || style.textAlign === "justify"
            ? style.textAlign : undefined) as any,
        letterSpacing: style.letterSpacing,

        // Visual Effects - nur PDF-kompatible
        overflow: style.overflow === "hidden" ? "hidden" : undefined,
        // Transform wurde bereits in Layout- und Sizing-Properties umgewandelt (translate → position/left/top, scale → width/height)
        // Andere Transforms wie rotate können nicht direkt übertragen werden
    };

    // Alle undefined Properties entfernen
    return removeUndefinedProperties(rawPdfStyle);
};

// Erstelle PDF-StyleSheet aus React.CSSProperties
export const createPDFStyleSheet = (style: React.CSSProperties): any => {
    const transformParsed = parseTransformForPDF(style.transform, style.width, style.height);
    const cleanedPdfStyle = convertCSSPropertiesForPDF(style, transformParsed);


    const pdf = getPdfRenderer();
    if (!pdf) return null;

    const { StyleSheet } = pdf;

    return StyleSheet.create({
        element: cleanedPdfStyle
    }).element;
};
